import { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, ButtonGroup, Card, Col, Form, Row, Spinner, Table } from 'react-bootstrap';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useTheme } from '../contexts/ThemeContext';
import { localISODate } from '../utils/dates';
import { errorMessage, num, period, shortAmount, PERIODS, monthLabel, downloadCsv } from '../utils/reportHelpers';

import DateInput from './DateInput';
import { formatDate } from '../utils/dateFormat';
const COLORS = {
  light: { sales: '#0d6efd', profit: '#198754', grid: '#e9ecef', axis: '#6c757d' },
  dark: { sales: '#3d8bfd', profit: '#3fb27f', grid: '#3a3a38', axis: '#adb5bd' }
};

/**
 * Sales > Reports: sales (before GST) and gross profit for a period, month by month, by customer and by product.
 * Gross profit is sales less the cost of the lots sold; lines whose cost isn't known are left out of the margin.
 */
function SalesReports({ active, currency = 'INR' }) {
  const { theme } = useTheme();
  const colors = COLORS[theme] || COLORS.light;
  const [preset, setPreset] = useState('thisFy');
  const [range, setRange] = useState(() => period('thisFy').map(localISODate));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState('customers');

  const money = useMemo(() => {
    const f = new Intl.NumberFormat('en-IN', { style: 'currency', currency, maximumFractionDigits: 0 });
    return (v) => f.format(num(v));
  }, [currency]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: r } = await axios.get('/api/reports/sales', { params: { from: range[0], to: range[1] } });
      setData(r);
    } catch (error) {
      toast.error(errorMessage(error, 'Could not work out the report'));
    } finally {
      setLoading(false);
    }
  }, [range]);

  useEffect(() => { if (active) load(); }, [active, load]);

  if (!active) return null;

  const choose = (key) => {
    setPreset(key);
    setRange(period(key).map(localISODate));
  };
  const t = data?.totals;
  const rows = data ? data[view] : [];
  const margin = (v) => (v == null ? '-' : `${Number(v).toLocaleString('en-IN', { maximumFractionDigits: 1 })}%`);

  const exportRows = () => {
    if (view === 'customers') {
      downloadCsv(`sales-by-customer-${range[0]}-to-${range[1]}.csv`,
        ['Customer', 'Sales (before GST)', 'Share %', 'Cost', 'Gross profit', 'Margin %', 'Bills', 'Last bill'],
        rows.map((r) => [r.name, r.sales, r.share, r.cost, r.grossProfit, r.margin, r.bills, r.lastDate]));
    } else {
      downloadCsv(`sales-by-product-${range[0]}-to-${range[1]}.csv`,
        ['Product', 'Quantity', 'Unit', 'Sales (before GST)', 'Average rate', 'Share %', 'Cost', 'Gross profit', 'Margin %'],
        rows.map((r) => [r.name, r.quantity, r.unit, r.sales, r.averageRate, r.share, r.cost, r.grossProfit, r.margin]));
    }
  };

  return (
    <div className="mt-3">
      <div className="d-flex flex-wrap align-items-end gap-2 mb-3">
        <ButtonGroup size="sm">
          {PERIODS.map(([key, label]) => (
            <Button key={key} variant={preset === key ? 'secondary' : 'outline-secondary'} onClick={() => choose(key)}>{label}</Button>
          ))}
        </ButtonGroup>
        <div className="d-flex align-items-center gap-1 ms-md-2">
          <DateInput size="sm" value={range[0]} max={range[1]} style={{ width: 'auto' }}
            onChange={(e) => { setPreset(''); setRange([e.target.value, range[1]]); }} />
          <span className="small text-muted">to</span>
          <DateInput size="sm" value={range[1]} min={range[0]} style={{ width: 'auto' }}
            onChange={(e) => { setPreset(''); setRange([range[0], e.target.value]); }} />
        </div>
        {loading && <Spinner animation="border" size="sm" />}
      </div>

      {t && (
        <>
          <Row className="g-3 mb-3">
            {[
              ['Sales (before GST)', money(t.sales)],
              ['Gross profit', money(t.grossProfit)],
              ['Margin', margin(t.margin)],
              ['Bills', `${t.bills}${t.averageBill ? ` · avg ${money(t.averageBill)}` : ''}`],
              ['Customers', t.customers]
            ].map(([label, value]) => (
              <Col key={label} xs={6} md>
                <Card className="text-center h-100"><Card.Body className="py-2">
                  <div className="fs-5 fw-semibold">{value}</div>
                  <div className="small text-muted">{label}</div>
                </Card.Body></Card>
              </Col>
            ))}
          </Row>
          {num(t.costUnknown) > 0 && (
            <Alert variant="light" className="small py-2">
              {money(t.costUnknown)} of sales came from lots with no cost recorded, so it is left out of the gross profit and margin.
            </Alert>
          )}

          {data.months.length > 1 && (
            <Card className="mb-3">
              <Card.Header className="py-2"><strong>Month by month</strong></Card.Header>
              <Card.Body style={{ height: 260 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.months.map((m) => ({ ...m, label: monthLabel(m.key), sales: num(m.sales), grossProfit: num(m.grossProfit) }))}
                    margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={colors.grid} vertical={false} />
                    <XAxis dataKey="label" tick={{ fontSize: 12, fill: colors.axis }} tickLine={false} axisLine={{ stroke: colors.grid }} />
                    <YAxis tick={{ fontSize: 12, fill: colors.axis }} axisLine={false} tickLine={false} width={70}
                      tickFormatter={shortAmount} />
                    <Tooltip formatter={(v, name) => [money(v), name === 'sales' ? 'Sales' : 'Gross profit']} />
                    <Legend formatter={(v) => (v === 'sales' ? 'Sales' : 'Gross profit')} />
                    <Bar dataKey="sales" fill={colors.sales} radius={[3, 3, 0, 0]} />
                    <Bar dataKey="grossProfit" fill={colors.profit} radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </Card.Body>
            </Card>
          )}

          <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
            <ButtonGroup size="sm">
              <Button variant={view === 'customers' ? 'primary' : 'outline-primary'} onClick={() => setView('customers')}>By customer</Button>
              <Button variant={view === 'products' ? 'primary' : 'outline-primary'} onClick={() => setView('products')}>By product</Button>
            </ButtonGroup>
            <Button size="sm" variant="outline-secondary" className="ms-auto" disabled={rows.length === 0} onClick={exportRows}>
              <i className="bi bi-download me-1" />Download CSV
            </Button>
          </div>
          <Table striped bordered hover responsive size="sm" className="small">
            <thead>
              {view === 'customers' ? (
                <tr><th>Customer</th><th className="text-end">Sales</th><th className="text-end">Share</th><th className="text-end">Gross profit</th>
                  <th className="text-end">Margin</th><th className="text-end">Bills</th><th>Last bill</th></tr>
              ) : (
                <tr><th>Product</th><th className="text-end">Quantity</th><th className="text-end">Sales</th><th className="text-end">Avg rate</th>
                  <th className="text-end">Share</th><th className="text-end">Gross profit</th><th className="text-end">Margin</th></tr>
              )}
            </thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={7} className="text-center text-muted py-3">No sales in this period.</td></tr>}
              {rows.map((r) => (view === 'customers' ? (
                <tr key={r.key}>
                  <td>{r.name}</td>
                  <td className="text-end text-nowrap">{money(r.sales)}</td>
                  <td className="text-end">{margin(r.share)}</td>
                  <td className="text-end text-nowrap">{money(r.grossProfit)}</td>
                  <td className={`text-end ${num(r.margin) < 0 ? 'text-danger' : ''}`}>{margin(r.margin)}</td>
                  <td className="text-end">{r.bills}</td>
                  <td className="text-nowrap">{formatDate(r.lastDate) || '-'}</td>
                </tr>
              ) : (
                <tr key={r.key}>
                  <td>{r.name}</td>
                  <td className="text-end text-nowrap">{r.quantity ? `${Number(r.quantity).toLocaleString('en-IN', { maximumFractionDigits: 3 })} ${r.unit || ''}` : '-'}</td>
                  <td className="text-end text-nowrap">{money(r.sales)}</td>
                  <td className="text-end text-nowrap">{r.averageRate != null ? money(r.averageRate) : '-'}</td>
                  <td className="text-end">{margin(r.share)}</td>
                  <td className="text-end text-nowrap">{money(r.grossProfit)}</td>
                  <td className={`text-end ${num(r.margin) < 0 ? 'text-danger' : ''}`}>{margin(r.margin)}</td>
                </tr>
              )))}
            </tbody>
          </Table>
          <p className="small text-muted">
            Sales are before GST, net of credit notes and with debit notes. Gross profit is sales less the cost of the stock sold (the cost of
            each lot as received, with services added); goods returned on a credit note come off both.
          </p>
        </>
      )}
    </div>
  );
}

export default SalesReports;
